import React from 'react'
import { useNavigate } from 'react-router-dom'
import { PanelLeftOpen, Shield, School, Star, CheckCircle, AlertTriangle, Crown } from 'lucide-react'
import Logo from '../common/Logo'
import { useSidebar } from '../../contexts/SidebarContext'
import { useSubscription } from '../../contexts/SubscriptionContext'
import { useAuth } from '../../contexts/AuthContext'
import { sidebarGroups, useSidebarNavigation, type SidebarItem } from './sidebarNav'

const railButton = 'w-10 h-10 grid place-items-center rounded-caderno-sm transition-colors'
const railIdle =
  'text-ink-500 dark:text-ink-300 hover:bg-cream-200 dark:hover:bg-ink-700/50 hover:text-ink-900 dark:hover:text-cream-100'
const railActive = 'bg-ink-900 text-cream-100 dark:bg-cream-100 dark:text-ink-900'
const railDanger = 'bg-blood-500 text-cream-50'

/**
 * Trilho de 64px: só ícones. Rótulos vivem no peek (hover/foco abre o painel
 * expandido por cima), por isso não há tooltip. Item com filhos abre o peek.
 */
const SidebarRail: React.FC = () => {
  const { toggle, startPeek, endPeek } = useSidebar()
  const { isAdmin, profile } = useAuth()
  const hasSchool = !!profile?.school_id
  const { isActive, isItemActive, isDisabledForNonAdmin, navigateTo } = useSidebarNavigation(endPeek)
  const navigate = useNavigate()

  const onItemClick = (item: SidebarItem) => {
    if (item.children) {
      startPeek(0)
      return
    }
    if (item.path) navigateTo(item.path, item.premium, item.adminOnly)
  }

  return (
    <div className="h-full flex flex-col items-center p-3 gap-1">
      <div className="py-1">
        <Logo variant="symbol" size={32} decorative />
      </div>

      <button
        type="button"
        onClick={toggle}
        aria-label="Expandir navegação"
        aria-expanded={false}
        className={`${railButton} ${railIdle}`}
      >
        <PanelLeftOpen className="w-5 h-5" />
      </button>

      <nav aria-label="Navegação principal" className="flex-1 w-full flex flex-col items-center gap-1 mt-2 overflow-y-auto">
        {sidebarGroups.map((group, groupIdx) => (
          <React.Fragment key={group.id}>
            {groupIdx > 0 && <div role="separator" className="w-8 border-t border-cream-400 dark:border-ink-500 my-3" />}
            {group.items.map((item) => {
              const Icon = item.icon
              const disabled = isDisabledForNonAdmin(item)
              const active = isItemActive(item)
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onItemClick(item)}
                  disabled={disabled}
                  aria-label={item.label}
                  aria-current={active ? 'page' : undefined}
                  className={`${railButton} ${active ? railActive : railIdle} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <Icon className="w-5 h-5" />
                </button>
              )
            })}
          </React.Fragment>
        ))}

        {hasSchool && (
          <>
            <div role="separator" className="w-8 border-t border-cream-400 dark:border-ink-500 my-3" />
            <button
              type="button"
              onClick={() => navigateTo('/app/escola')}
              aria-label="Minha escola"
              title="Minha escola"
              aria-current={isActive('/app/escola') ? 'page' : undefined}
              className={`${railButton} ${isActive('/app/escola') ? railActive : railIdle}`}
            >
              <School className="w-5 h-5" />
            </button>
          </>
        )}

        {isAdmin && (
          <>
            <div role="separator" className="w-8 border-t border-cream-400 dark:border-ink-500 my-3" />
            <button
              type="button"
              onClick={() => { navigate('/admin'); endPeek() }}
              aria-label="Painel de Admin"
              aria-current={isActive('/admin') ? 'page' : undefined}
              className={`${railButton} ${isActive('/admin') ? railDanger : railIdle}`}
            >
              <Shield className="w-5 h-5" />
            </button>
          </>
        )}
      </nav>

      <RailPlanIndicator />
    </div>
  )
}

/** Versão compacta do SubscriptionStatusCard: ícone por fase, dias no trial. */
const RailPlanIndicator: React.FC = () => {
  const navigate = useNavigate()
  const { subscriptionPhase, daysLeftInTrial } = useSubscription()
  const { isSchoolAdmin } = useAuth()

  if (isSchoolAdmin || subscriptionPhase === 'escolar') {
    const label = isSchoolAdmin ? 'Plano Escolar — coordenação' : 'Plano Escolar'
    return (
      <div className={`${railButton} bg-terra-50 dark:bg-terra-500/10 text-terra-500`} aria-label={label} title={label}>
        <School className="w-5 h-5" />
      </div>
    )
  }

  if (subscriptionPhase === 'premium') {
    return (
      <div className={`${railButton} bg-moss-100 dark:bg-moss-500/20 text-moss-500`} aria-label="Plano Premium" title="Plano Premium">
        <CheckCircle className="w-5 h-5" />
      </div>
    )
  }

  const goToBilling = () => navigate('/app/billing')

  if (subscriptionPhase === 'trialing') {
    const days = daysLeftInTrial ?? 0
    const label = `Período de teste: ${days} ${days === 1 ? 'dia restante' : 'dias restantes'}. Assinar agora`
    return (
      <button
        type="button"
        onClick={goToBilling}
        aria-label={label}
        title="Período de teste — assinar agora"
        className="w-10 flex flex-col items-center gap-1 py-1 rounded-caderno-sm bg-ochre-100 dark:bg-ochre-500/20 text-ink-900 dark:text-cream-50 hover:bg-ochre-100/70 dark:hover:bg-ochre-500/30 transition-colors"
      >
        <Star className="w-4 h-4 text-ochre-500" />
        <span className="font-mono text-xs leading-none">{days}d</span>
      </button>
    )
  }

  if (subscriptionPhase === 'trial_expired') {
    return (
      <button
        type="button"
        onClick={goToBilling}
        aria-label="Seu período de teste terminou. Ver planos"
        title="Ver planos"
        className={`${railButton} bg-blood-100 dark:bg-blood-500/20 text-blood-500 hover:bg-blood-100/70`}
      >
        <AlertTriangle className="w-5 h-5" />
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={goToBilling}
      aria-label="Escolha um plano"
      title="Escolha um plano"
      className={`${railButton} bg-terra-50 dark:bg-terra-500/10 text-terra-500 hover:bg-terra-100`}
    >
      <Crown className="w-5 h-5" />
    </button>
  )
}

export default SidebarRail
